import { Injectable } from '@angular/core';
import { LocaleService } from './locale.service';

type UiLocale = 'en' | 'fr' | 'es' | 'de' | 'pt-br' | 'ja' | 'zh-chs';

const MESSAGES: Record<UiLocale, Record<string, string>> = {
  en: {
    'tab.activities': 'Activities',
    'tab.firsts': 'Guardian Firsts',
    'tab.titles': 'Titles',
    'tab.accomplishments': 'Accomplishments',
    'tab.breakdown': 'Activity Breakdown',
    'tab.heatmap': 'Heatmap',
    'tab.playedWith': 'Played With',
    'firsts.pantheonEvents': 'Pantheon Events',
    'breakdown.activity': 'Activity',
    'breakdown.variant': 'Variant',
    'breakdown.game': 'Game',
    'breakdown.lastPlayed': 'Last played',
    'breakdown.lastActivityDate': 'Last activity',
    'breakdown.elapsedSince': 'Elapsed',
    'breakdown.runsClearsTime': 'Runs / Clears / Time',
    'activities.collapseAllYears': 'Collapse all years',
    'activities.expandAllYears': 'Expand all years',
    'activities.show': 'Show',
    'activities.hide': 'Hide',
    'activities.expand': 'Expand',
    'activities.collapse': 'Collapse',
    'activities.viewCards': 'Cards',
    'activities.viewChronological': 'Chronological',
    'activities.sortOldest': 'Oldest first',
    'activities.sortNewest': 'Newest first',
    'pgcr.title': 'Activity report',
    'pgcr.loading': 'Loading fireteam…',
    'pgcr.error': 'Could not load activity report.',
    'pgcr.errorRetry': 'Failed to load PGCR. Try again or open the full report.',
    'pgcr.noPlayers': 'No player data in this report.',
    'pgcr.close': 'Close',
    'pgcr.full': 'Full PGCR ↗',
    'pgcr.bungie': 'Open on Bungie ↗',
    'pgcr.ctrlHint': 'Ctrl+click for full PGCR',
    'pgcr.openReportHint': 'Opens activity report',
    'common.close': 'Close',
    'archive.offlineBanner': 'Offline on this device — read-only. Saved',
    'archive.exitOffline': 'Exit offline mode',
    'archive.saveOnDevice': 'Save for offline on this device',
    'archive.saveOnDeviceHint': 'Use while online after your profiles have loaded. No file download — works in this browser only.',
    'archive.deviceReady': 'This device is ready for offline use. Bookmark this page or install the app (see below).',
    'archive.installAppHint': 'Tip: In Chrome or Edge, use Install app in the address bar or browser menu for a desktop icon.',
    'archive.backupSection': 'Backup or another computer',
    'archive.build': 'Download backup (.zip)',
    'archive.buildLite': 'Download lite backup (no PGCR fireteams)',
    'archive.buildHtmlReport': 'Download full HTML report',
    'archive.open': 'Open backup file…',
    'archive.update': 'Download updated backup',
    'archive.exportStarting': 'Starting archive export…',
    'archive.importStarting': 'Importing archive…',
    'archive.exportFailed': 'Archive export failed.',
    'archive.importFailed': 'Could not import archive file.',
    'archive.needProfiles': 'Select at least one profile before building an archive.',
    'archive.offlineSearchDisabled': 'Player search is disabled in offline archive mode.',
    'archive.pgcrNotArchived': 'This activity report was not included in the archive.',
    'archive.exportProgress': 'Exporting archive…',
    'archive.checkUpdates': 'Check for updates',
    'archive.checkingUpdates': 'Checking Bungie for new activities…',
    'archive.bungieOffline': 'Bungie API is unreachable. Connect to the internet and try again.',
    'archive.upToDate': 'Archive is up to date — no new activities since last sync.',
    'archive.updateSuccess': 'Archive refreshed with new activities.',
    'archive.updateFailed': 'Could not update archive. Try again later.',
    'archive.syncingAccount': 'Syncing',
    'archive.offlineSectionTitle': 'Offline on this device',
    'archive.d1NamesEnglish': 'Destiny 1 activity names stay in English in offline mode.',
    'archive.progress.prepareDevice': 'Preparing offline copy on this device…',
    'archive.progress.savingImages': 'Saving images for offline viewing…',
    'archive.progress.activatingOffline': 'Activating offline mode…',
    'archive.progress.readyDevice': 'Ready for offline use on this device.',
    'archive.progress.buildingZip': 'Building archive file…',
    'archive.progress.collectingActivities': 'Collecting activities…',
    'archive.progress.collectingFavorites': 'Collecting favorites…',
    'archive.progress.buildingManifestSubset': 'Building manifest subset…',
    'archive.progress.snapshottingTitles': 'Snapshotting titles…',
    'archive.progress.collectingPgcr': 'Collecting PGCR cache…',
    'archive.progress.collectingImageUrls': 'Collecting image URLs…',
    'archive.progress.downloadingImages': 'Downloading images ({done}/{total})…',
    'archive.progress.savingDownload': 'Saving download…',
    'archive.progress.exportComplete': 'Archive export complete.',
    'playedWith.intro': 'Guardians you have played with on the selected Destiny 2 account.',
    'playedWith.account': 'Account',
    'playedWith.d2Only': 'Played With covers Destiny 2 accounts. Destiny 1 accounts are not listed here.',
    'playedWith.coverage': 'Activity coverage',
    'playedWith.downloading': 'Downloading more reports…',
    'playedWith.remaining': 'remaining',
    'playedWith.loading': 'Loading Played With…',
    'playedWith.building': 'Building your Played With…',
    'playedWith.buildTimeout': 'Your Played With summary is taking longer than expected. Please refresh the page in a few minutes to try again.',
    'playedWith.buildingHint': 'This can take a few minutes for veteran accounts. Check back shortly — we will keep trying automatically.',
    'playedWith.empty': 'No activity history is available yet for Played With.',
    'playedWith.noPartners': 'No partners found in the activities covered so far.',
    'playedWith.player': 'Player',
    'playedWith.activities': 'Activities',
    'playedWith.time': 'Time together',
    'playedWith.firstPlayed': 'First played',
    'playedWith.lastPlayed': 'Last played',
    'archive.progress.updatePrefix': 'Update: {message}'
  },
  fr: {
    'tab.activities': 'Activités',
    'tab.firsts': 'Premières fois',
    'tab.titles': 'Titres',
    'tab.accomplishments': 'Réalisations',
    'tab.breakdown': 'Répartition',
    'tab.heatmap': 'Carte thermique',
    'breakdown.activity': 'Activité',
    'breakdown.variant': 'Variante',
    'breakdown.game': 'Jeu',
    'breakdown.lastPlayed': 'Dernière partie',
    'breakdown.lastActivityDate': 'Dernière activité',
    'breakdown.elapsedSince': 'Écoulé',
    'breakdown.runsClearsTime': 'Parties / Réussites / Temps',
    'activities.collapseAllYears': 'Replier toutes les années',
    'activities.expandAllYears': 'Déplier toutes les années',
    'activities.show': 'Afficher',
    'activities.hide': 'Masquer',
    'activities.expand': 'Déplier',
    'activities.collapse': 'Replier',
    'activities.viewCards': 'Cartes',
    'activities.viewChronological': 'Chronologique',
    'activities.sortOldest': 'Plus ancien',
    'activities.sortNewest': 'Plus récent',
    'pgcr.title': 'Rapport d\'activité',
    'pgcr.loading': 'Chargement de l\'escouade…',
    'pgcr.error': 'Impossible de charger le rapport.',
    'pgcr.errorRetry': 'Échec du chargement. Réessayez ou ouvrez le rapport complet.',
    'pgcr.noPlayers': 'Aucune donnée joueur dans ce rapport.',
    'pgcr.close': 'Fermer',
    'pgcr.full': 'PGCR complet ↗',
    'pgcr.bungie': 'Ouvrir sur Bungie ↗',
    'pgcr.ctrlHint': 'Ctrl+clic pour le PGCR complet',
    'pgcr.openReportHint': 'Ouvre le rapport d\'activité',
    'common.close': 'Fermer',
    'archive.offlineBanner': 'Hors ligne sur cet appareil — lecture seule. Enregistré le',
    'archive.exitOffline': 'Quitter le mode hors ligne',
    'archive.saveOnDevice': 'Enregistrer pour le hors ligne sur cet appareil',
    'archive.saveOnDeviceHint': 'À utiliser en ligne après le chargement de vos profils. Aucun téléchargement — fonctionne uniquement dans ce navigateur.',
    'archive.deviceReady': 'Cet appareil est prêt pour le hors ligne. Ajoutez cette page aux favoris ou installez l\'app (voir ci-dessous).',
    'archive.installAppHint': 'Astuce : dans Chrome ou Edge, utilisez Installer l\'application dans la barre d\'adresse ou le menu du navigateur.',
    'archive.backupSection': 'Sauvegarde ou autre ordinateur',
    'archive.build': 'Télécharger la sauvegarde (.zip)',
    'archive.buildLite': 'Télécharger la sauvegarde légère (sans escouades PGCR)',
    'archive.buildHtmlReport': 'Télécharger le rapport HTML complet',
    'archive.open': 'Ouvrir un fichier de sauvegarde…',
    'archive.update': 'Télécharger la sauvegarde mise à jour',
    'archive.exportStarting': 'Démarrage de l\'export de l\'archive…',
    'archive.importStarting': 'Importation de l\'archive…',
    'archive.exportFailed': 'Échec de l\'export de l\'archive.',
    'archive.importFailed': 'Impossible d\'importer le fichier d\'archive.',
    'archive.needProfiles': 'Sélectionnez au moins un profil avant de créer une archive.',
    'archive.offlineSearchDisabled': 'La recherche de joueurs est désactivée en mode archive hors ligne.',
    'archive.pgcrNotArchived': 'Ce rapport d\'activité n\'a pas été inclus dans l\'archive.',
    'archive.exportProgress': 'Exportation de l\'archive…',
    'archive.checkUpdates': 'Vérifier les mises à jour',
    'archive.checkingUpdates': 'Vérification des nouvelles activités sur Bungie…',
    'archive.bungieOffline': 'L\'API Bungie est inaccessible. Connectez-vous à Internet et réessayez.',
    'archive.upToDate': 'Archive à jour — aucune nouvelle activité depuis la dernière synchronisation.',
    'archive.updateSuccess': 'Archive actualisée avec de nouvelles activités.',
    'archive.updateFailed': 'Impossible de mettre à jour l\'archive. Réessayez plus tard.',
    'archive.syncingAccount': 'Synchronisation',
    'archive.offlineSectionTitle': 'Hors ligne sur cet appareil',
    'archive.d1NamesEnglish': 'Les noms d\'activités Destiny 1 restent en anglais en mode hors ligne.',
    'archive.progress.prepareDevice': 'Préparation de la copie hors ligne sur cet appareil…',
    'archive.progress.savingImages': 'Enregistrement des images pour consultation hors ligne…',
    'archive.progress.activatingOffline': 'Activation du mode hors ligne…',
    'archive.progress.readyDevice': 'Prêt pour le hors ligne sur cet appareil.',
    'archive.progress.buildingZip': 'Création du fichier d\'archive…',
    'archive.progress.collectingActivities': 'Collecte des activités…',
    'archive.progress.collectingFavorites': 'Collecte des favoris…',
    'archive.progress.buildingManifestSubset': 'Création du sous-ensemble du manifeste…',
    'archive.progress.snapshottingTitles': 'Capture instantanée des titres…',
    'archive.progress.collectingPgcr': 'Collecte du cache PGCR…',
    'archive.progress.collectingImageUrls': 'Collecte des URL d\'images…',
    'archive.progress.downloadingImages': 'Téléchargement des images ({done}/{total})…',
    'archive.progress.savingDownload': 'Enregistrement du téléchargement…',
    'archive.progress.exportComplete': 'Export de l\'archive terminé.',
    'archive.progress.updatePrefix': 'Mise à jour : {message}'
  },
  es: {
    'tab.activities': 'Actividades',
    'tab.firsts': 'Primeras veces',
    'tab.titles': 'Títulos',
    'tab.accomplishments': 'Logros',
    'tab.breakdown': 'Desglose',
    'tab.heatmap': 'Mapa de calor',
    'breakdown.activity': 'Actividad',
    'breakdown.variant': 'Variante',
    'breakdown.game': 'Juego',
    'breakdown.lastPlayed': 'Última partida',
    'breakdown.lastActivityDate': 'Última actividad',
    'breakdown.elapsedSince': 'Transcurrido',
    'breakdown.runsClearsTime': 'Partidas / Victorias / Tiempo',
    'activities.collapseAllYears': 'Contraer todos los años',
    'activities.expandAllYears': 'Expandir todos los años',
    'activities.show': 'Mostrar',
    'activities.hide': 'Ocultar',
    'activities.expand': 'Expandir',
    'activities.collapse': 'Contraer',
    'activities.viewCards': 'Tarjetas',
    'activities.viewChronological': 'Cronológico',
    'activities.sortOldest': 'Más antiguo',
    'activities.sortNewest': 'Más reciente',
    'pgcr.title': 'Informe de actividad',
    'pgcr.loading': 'Cargando equipo…',
    'pgcr.error': 'No se pudo cargar el informe.',
    'pgcr.errorRetry': 'Error al cargar. Inténtalo de nuevo o abre el informe completo.',
    'pgcr.noPlayers': 'Sin datos de jugadores en este informe.',
    'pgcr.close': 'Cerrar',
    'pgcr.full': 'PGCR completo ↗',
    'pgcr.bungie': 'Abrir en Bungie ↗',
    'pgcr.ctrlHint': 'Ctrl+clic para PGCR completo',
    'pgcr.openReportHint': 'Abre el informe de actividad',
    'common.close': 'Cerrar',
    'archive.offlineBanner': 'Sin conexión en este dispositivo — solo lectura. Guardado el',
    'archive.exitOffline': 'Salir del modo sin conexión',
    'archive.saveOnDevice': 'Guardar para sin conexión en este dispositivo',
    'archive.saveOnDeviceHint': 'Úsalo en línea después de cargar tus perfiles. Sin descarga de archivo — solo en este navegador.',
    'archive.deviceReady': 'Este dispositivo está listo para uso sin conexión. Marca esta página o instala la app (abajo).',
    'archive.installAppHint': 'Consejo: en Chrome o Edge, usa Instalar aplicación en la barra de direcciones o el menú del navegador.',
    'archive.backupSection': 'Copia de seguridad u otro equipo',
    'archive.build': 'Descargar copia de seguridad (.zip)',
    'archive.buildLite': 'Descargar copia ligera (sin escuadras PGCR)',
    'archive.buildHtmlReport': 'Descargar informe HTML completo',
    'archive.open': 'Abrir archivo de copia…',
    'archive.update': 'Descargar copia actualizada',
    'archive.exportStarting': 'Iniciando exportación del archivo…',
    'archive.importStarting': 'Importando archivo…',
    'archive.exportFailed': 'Error al exportar el archivo.',
    'archive.importFailed': 'No se pudo importar el archivo.',
    'archive.needProfiles': 'Selecciona al menos un perfil antes de crear un archivo.',
    'archive.offlineSearchDisabled': 'La búsqueda de jugadores está desactivada en modo archivo sin conexión.',
    'archive.pgcrNotArchived': 'Este informe de actividad no se incluyó en el archivo.',
    'archive.exportProgress': 'Exportando archivo…',
    'archive.checkUpdates': 'Buscar actualizaciones',
    'archive.checkingUpdates': 'Comprobando nuevas actividades en Bungie…',
    'archive.bungieOffline': 'La API de Bungie no está disponible. Conéctate a Internet e inténtalo de nuevo.',
    'archive.upToDate': 'El archivo está actualizado — sin actividades nuevas desde la última sincronización.',
    'archive.updateSuccess': 'Archivo actualizado con nuevas actividades.',
    'archive.updateFailed': 'No se pudo actualizar el archivo. Inténtalo más tarde.',
    'archive.syncingAccount': 'Sincronizando',
    'archive.offlineSectionTitle': 'Sin conexión en este dispositivo',
    'archive.d1NamesEnglish': 'Los nombres de actividades de Destiny 1 permanecen en inglés sin conexión.',
    'archive.progress.prepareDevice': 'Preparando copia sin conexión en este dispositivo…',
    'archive.progress.savingImages': 'Guardando imágenes para ver sin conexión…',
    'archive.progress.activatingOffline': 'Activando modo sin conexión…',
    'archive.progress.readyDevice': 'Listo para uso sin conexión en este dispositivo.',
    'archive.progress.buildingZip': 'Creando archivo…',
    'archive.progress.collectingActivities': 'Recopilando actividades…',
    'archive.progress.collectingFavorites': 'Recopilando favoritos…',
    'archive.progress.buildingManifestSubset': 'Creando subconjunto del manifiesto…',
    'archive.progress.snapshottingTitles': 'Capturando títulos…',
    'archive.progress.collectingPgcr': 'Recopilando caché PGCR…',
    'archive.progress.collectingImageUrls': 'Recopilando URL de imágenes…',
    'archive.progress.downloadingImages': 'Descargando imágenes ({done}/{total})…',
    'archive.progress.savingDownload': 'Guardando descarga…',
    'archive.progress.exportComplete': 'Exportación del archivo completada.',
    'archive.progress.updatePrefix': 'Actualización: {message}'
  },
  de: {
    'tab.activities': 'Aktivitäten',
    'tab.firsts': 'Erste Male',
    'tab.titles': 'Titel',
    'tab.accomplishments': 'Errungenschaften',
    'tab.breakdown': 'Übersicht',
    'tab.heatmap': 'Heatmap',
    'breakdown.activity': 'Aktivität',
    'breakdown.variant': 'Variante',
    'breakdown.game': 'Spiel',
    'breakdown.lastPlayed': 'Zuletzt gespielt',
    'breakdown.lastActivityDate': 'Letzte Aktivität',
    'breakdown.elapsedSince': 'Verstrichen',
    'breakdown.runsClearsTime': 'Läufe / Siege / Zeit',
    'activities.collapseAllYears': 'Alle Jahre einklappen',
    'activities.expandAllYears': 'Alle Jahre ausklappen',
    'activities.show': 'Anzeigen',
    'activities.hide': 'Ausblenden',
    'activities.expand': 'Ausklappen',
    'activities.collapse': 'Einklappen',
    'activities.viewCards': 'Karten',
    'activities.viewChronological': 'Chronologisch',
    'activities.sortOldest': 'Älteste zuerst',
    'activities.sortNewest': 'Neueste zuerst',
    'pgcr.title': 'Aktivitätsbericht',
    'pgcr.loading': 'Trupp wird geladen…',
    'pgcr.error': 'Bericht konnte nicht geladen werden.',
    'pgcr.errorRetry': 'Laden fehlgeschlagen. Erneut versuchen oder vollständigen Bericht öffnen.',
    'pgcr.noPlayers': 'Keine Spielerdaten in diesem Bericht.',
    'pgcr.close': 'Schließen',
    'pgcr.full': 'Vollständiger PGCR ↗',
    'pgcr.bungie': 'Auf Bungie öffnen ↗',
    'pgcr.ctrlHint': 'Strg+Klick für vollständigen PGCR',
    'pgcr.openReportHint': 'Aktivitätsbericht öffnen',
    'common.close': 'Schließen',
    'archive.offlineBanner': 'Offline auf diesem Gerät — nur Lesen. Gespeichert am',
    'archive.exitOffline': 'Offline-Modus beenden',
    'archive.saveOnDevice': 'Für Offline auf diesem Gerät speichern',
    'archive.saveOnDeviceHint': 'Online nutzen, nachdem deine Profile geladen sind. Kein Dateidownload — nur in diesem Browser.',
    'archive.deviceReady': 'Dieses Gerät ist offline bereit. Seite bookmarken oder App installieren (siehe unten).',
    'archive.installAppHint': 'Tipp: In Chrome oder Edge „App installieren“ in der Adressleiste oder im Browsermenü nutzen.',
    'archive.backupSection': 'Sicherung oder anderer Computer',
    'archive.build': 'Sicherung herunterladen (.zip)',
    'archive.buildLite': 'Lite-Sicherung herunterladen (ohne PGCR-Trupps)',
    'archive.buildHtmlReport': 'Vollständigen HTML-Bericht herunterladen',
    'archive.open': 'Sicherungsdatei öffnen…',
    'archive.update': 'Aktualisierte Sicherung herunterladen',
    'archive.exportStarting': 'Archiv-Export wird gestartet…',
    'archive.importStarting': 'Archiv wird importiert…',
    'archive.exportFailed': 'Archiv-Export fehlgeschlagen.',
    'archive.importFailed': 'Archivdatei konnte nicht importiert werden.',
    'archive.needProfiles': 'Wähle mindestens ein Profil, bevor du ein Archiv erstellst.',
    'archive.offlineSearchDisabled': 'Spielersuche ist im Offline-Archivmodus deaktiviert.',
    'archive.pgcrNotArchived': 'Dieser Aktivitätsbericht war nicht im Archiv enthalten.',
    'archive.exportProgress': 'Archiv wird exportiert…',
    'archive.checkUpdates': 'Nach Updates suchen',
    'archive.checkingUpdates': 'Bungie wird auf neue Aktivitäten geprüft…',
    'archive.bungieOffline': 'Bungie-API nicht erreichbar. Mit dem Internet verbinden und erneut versuchen.',
    'archive.upToDate': 'Archiv ist aktuell — keine neuen Aktivitäten seit der letzten Synchronisation.',
    'archive.updateSuccess': 'Archiv mit neuen Aktivitäten aktualisiert.',
    'archive.updateFailed': 'Archiv konnte nicht aktualisiert werden. Später erneut versuchen.',
    'archive.syncingAccount': 'Synchronisiere',
    'archive.offlineSectionTitle': 'Offline auf diesem Gerät',
    'archive.d1NamesEnglish': 'Destiny-1-Aktivitätsnamen bleiben offline auf Englisch.',
    'archive.progress.prepareDevice': 'Offline-Kopie auf diesem Gerät wird vorbereitet…',
    'archive.progress.savingImages': 'Bilder für Offline-Ansicht werden gespeichert…',
    'archive.progress.activatingOffline': 'Offline-Modus wird aktiviert…',
    'archive.progress.readyDevice': 'Bereit für Offline-Nutzung auf diesem Gerät.',
    'archive.progress.buildingZip': 'Archivdatei wird erstellt…',
    'archive.progress.collectingActivities': 'Aktivitäten werden gesammelt…',
    'archive.progress.collectingFavorites': 'Favoriten werden gesammelt…',
    'archive.progress.buildingManifestSubset': 'Manifest-Teilmenge wird erstellt…',
    'archive.progress.snapshottingTitles': 'Titel werden erfasst…',
    'archive.progress.collectingPgcr': 'PGCR-Cache wird gesammelt…',
    'archive.progress.collectingImageUrls': 'Bild-URLs werden gesammelt…',
    'archive.progress.downloadingImages': 'Bilder werden heruntergeladen ({done}/{total})…',
    'archive.progress.savingDownload': 'Download wird gespeichert…',
    'archive.progress.exportComplete': 'Archiv-Export abgeschlossen.',
    'archive.progress.updatePrefix': 'Update: {message}'
  },
  'pt-br': {
    'tab.activities': 'Atividades',
    'tab.firsts': 'Primeiras vezes',
    'tab.titles': 'Títulos',
    'tab.accomplishments': 'Conquistas',
    'tab.breakdown': 'Resumo',
    'tab.heatmap': 'Mapa de calor',
    'breakdown.activity': 'Atividade',
    'breakdown.variant': 'Variante',
    'breakdown.game': 'Jogo',
    'breakdown.lastPlayed': 'Última partida',
    'breakdown.lastActivityDate': 'Última actividad',
    'breakdown.elapsedSince': 'Transcurrido',
    'breakdown.runsClearsTime': 'Partidas / Vitórias / Tempo',
    'activities.collapseAllYears': 'Recolher todos os anos',
    'activities.expandAllYears': 'Expandir todos os anos',
    'activities.show': 'Mostrar',
    'activities.hide': 'Ocultar',
    'activities.expand': 'Expandir',
    'activities.collapse': 'Recolher',
    'activities.viewCards': 'Cartões',
    'activities.viewChronological': 'Cronológico',
    'activities.sortOldest': 'Mais antigo',
    'activities.sortNewest': 'Mais recente',
    'pgcr.title': 'Relatório de atividade',
    'pgcr.loading': 'Carregando equipe…',
    'pgcr.error': 'Não foi possível carregar o relatório.',
    'pgcr.errorRetry': 'Falha ao carregar. Tente novamente ou abra o relatório completo.',
    'pgcr.noPlayers': 'Sem dados de jogadores neste relatório.',
    'pgcr.close': 'Fechar',
    'pgcr.full': 'PGCR completo ↗',
    'pgcr.bungie': 'Abrir no Bungie ↗',
    'pgcr.ctrlHint': 'Ctrl+clique para PGCR completo',
    'pgcr.openReportHint': 'Abre o relatório de atividade',
    'common.close': 'Fechar',
    'archive.offlineBanner': 'Offline neste dispositivo — somente leitura. Salvo em',
    'archive.exitOffline': 'Sair do modo offline',
    'archive.saveOnDevice': 'Salvar para offline neste dispositivo',
    'archive.saveOnDeviceHint': 'Use online após carregar seus perfis. Sem download de arquivo — funciona só neste navegador.',
    'archive.deviceReady': 'Este dispositivo está pronto para offline. Favorite esta página ou instale o app (abaixo).',
    'archive.installAppHint': 'Dica: no Chrome ou Edge, use Instalar aplicativo na barra de endereços ou no menu do navegador.',
    'archive.backupSection': 'Backup ou outro computador',
    'archive.build': 'Baixar backup (.zip)',
    'archive.buildLite': 'Baixar backup leve (sem equipes PGCR)',
    'archive.buildHtmlReport': 'Baixar relatório HTML completo',
    'archive.open': 'Abrir arquivo de backup…',
    'archive.update': 'Baixar backup atualizado',
    'archive.exportStarting': 'Iniciando exportação do arquivo…',
    'archive.importStarting': 'Importando arquivo…',
    'archive.exportFailed': 'Falha na exportação do arquivo.',
    'archive.importFailed': 'Não foi possível importar o arquivo.',
    'archive.needProfiles': 'Selecione pelo menos um perfil antes de criar um arquivo.',
    'archive.offlineSearchDisabled': 'A busca de jogadores está desativada no modo arquivo offline.',
    'archive.pgcrNotArchived': 'Este relatório de atividade não foi incluído no arquivo.',
    'archive.exportProgress': 'Exportando arquivo…',
    'archive.checkUpdates': 'Verificar atualizações',
    'archive.checkingUpdates': 'Verificando novas atividades na Bungie…',
    'archive.bungieOffline': 'A API da Bungie está inacessível. Conecte-se à internet e tente novamente.',
    'archive.upToDate': 'Arquivo atualizado — sem novas atividades desde a última sincronização.',
    'archive.updateSuccess': 'Arquivo atualizado com novas atividades.',
    'archive.updateFailed': 'Não foi possível atualizar o arquivo. Tente novamente mais tarde.',
    'archive.syncingAccount': 'Sincronizando',
    'archive.offlineSectionTitle': 'Offline neste dispositivo',
    'archive.d1NamesEnglish': 'Nomes de atividades do Destiny 1 permanecem em inglês no modo offline.',
    'archive.progress.prepareDevice': 'Preparando cópia offline neste dispositivo…',
    'archive.progress.savingImages': 'Salvando imagens para visualização offline…',
    'archive.progress.activatingOffline': 'Ativando modo offline…',
    'archive.progress.readyDevice': 'Pronto para uso offline neste dispositivo.',
    'archive.progress.buildingZip': 'Criando arquivo…',
    'archive.progress.collectingActivities': 'Coletando atividades…',
    'archive.progress.collectingFavorites': 'Coletando favoritos…',
    'archive.progress.buildingManifestSubset': 'Criando subconjunto do manifesto…',
    'archive.progress.snapshottingTitles': 'Capturando títulos…',
    'archive.progress.collectingPgcr': 'Coletando cache PGCR…',
    'archive.progress.collectingImageUrls': 'Coletando URLs de imagens…',
    'archive.progress.downloadingImages': 'Baixando imagens ({done}/{total})…',
    'archive.progress.savingDownload': 'Salvando download…',
    'archive.progress.exportComplete': 'Exportação do arquivo concluída.',
    'archive.progress.updatePrefix': 'Atualização: {message}'
  },
  ja: {
    'tab.activities': 'アクティビティ',
    'tab.firsts': '初クリア',
    'tab.titles': '称号',
    'tab.accomplishments': '実績',
    'tab.breakdown': '内訳',
    'tab.heatmap': 'ヒートマップ',
    'breakdown.activity': 'アクティビティ',
    'breakdown.variant': 'バリアント',
    'breakdown.game': 'ゲーム',
    'breakdown.lastPlayed': '最終プレイ',
    'breakdown.lastActivityDate': '最終アクティビティ',
    'breakdown.elapsedSince': '経過',
    'breakdown.runsClearsTime': '回数 / クリア / 時間',
    'activities.collapseAllYears': '全年を折りたたむ',
    'activities.expandAllYears': '全年を展開',
    'activities.show': '表示',
    'activities.hide': '非表示',
    'activities.expand': '展開',
    'activities.collapse': '折りたたむ',
    'activities.viewCards': 'カード',
    'activities.viewChronological': '年表',
    'activities.sortOldest': '古い順',
    'activities.sortNewest': '新しい順',
    'pgcr.title': 'アクティビティレポート',
    'pgcr.loading': 'メンバーを読み込み中…',
    'pgcr.error': 'レポートを読み込めませんでした。',
    'pgcr.errorRetry': '読み込みに失敗しました。再試行するか完全版を開いてください。',
    'pgcr.noPlayers': 'このレポートにプレイヤーデータがありません。',
    'pgcr.close': '閉じる',
    'pgcr.full': '完全な PGCR ↗',
    'pgcr.bungie': 'Bungie で開く ↗',
    'pgcr.ctrlHint': 'Ctrl+クリックで完全な PGCR',
    'pgcr.openReportHint': 'アクティビティレポートを開く',
    'common.close': '閉じる',
    'archive.offlineBanner': 'この端末はオフライン — 読み取り専用。保存日',
    'archive.exitOffline': 'オフラインモードを終了',
    'archive.saveOnDevice': 'この端末でオフライン用に保存',
    'archive.saveOnDeviceHint': 'プロフィール読み込み後、オンライン時に使用。ファイルのダウンロードなし — このブラウザのみ。',
    'archive.deviceReady': 'この端末はオフライン利用の準備ができました。ブックマークまたはアプリをインストール（下記）。',
    'archive.installAppHint': 'ヒント：Chrome または Edge で、アドレスバーまたはメニューからアプリをインストール。',
    'archive.backupSection': 'バックアップまたは別の PC',
    'archive.build': 'バックアップをダウンロード (.zip)',
    'archive.buildLite': 'ライトバックアップをダウンロード（PGCR なし）',
    'archive.buildHtmlReport': '完全な HTML レポートをダウンロード',
    'archive.open': 'バックアップファイルを開く…',
    'archive.update': '更新されたバックアップをダウンロード',
    'archive.exportStarting': 'アーカイブのエクスポートを開始…',
    'archive.importStarting': 'アーカイブをインポート中…',
    'archive.exportFailed': 'アーカイブのエクスポートに失敗しました。',
    'archive.importFailed': 'アーカイブファイルをインポートできませんでした。',
    'archive.needProfiles': 'アーカイブを作成する前に、少なくとも1つのプロフィールを選択してください。',
    'archive.offlineSearchDisabled': 'オフラインアーカイブモードではプレイヤー検索は無効です。',
    'archive.pgcrNotArchived': 'このアクティビティレポートはアーカイブに含まれていません。',
    'archive.exportProgress': 'アーカイブをエクスポート中…',
    'archive.checkUpdates': '更新を確認',
    'archive.checkingUpdates': 'Bungie で新しいアクティビティを確認中…',
    'archive.bungieOffline': 'Bungie API に接続できません。インターネットに接続して再試行してください。',
    'archive.upToDate': 'アーカイブは最新です — 前回の同期以降、新しいアクティビティはありません。',
    'archive.updateSuccess': '新しいアクティビティでアーカイブを更新しました。',
    'archive.updateFailed': 'アーカイブを更新できませんでした。後でもう一度お試しください。',
    'archive.syncingAccount': '同期中',
    'archive.offlineSectionTitle': 'この端末でオフライン',
    'archive.d1NamesEnglish': 'オフラインモードでは Destiny 1 のアクティビティ名は英語のままです。',
    'archive.progress.prepareDevice': 'この端末のオフラインコピーを準備中…',
    'archive.progress.savingImages': 'オフライン表示用に画像を保存中…',
    'archive.progress.activatingOffline': 'オフラインモードを有効化中…',
    'archive.progress.readyDevice': 'この端末でオフライン利用の準備ができました。',
    'archive.progress.buildingZip': 'アーカイブファイルを作成中…',
    'archive.progress.collectingActivities': 'アクティビティを収集中…',
    'archive.progress.collectingFavorites': 'お気に入りを収集中…',
    'archive.progress.buildingManifestSubset': 'マニフェストのサブセットを作成中…',
    'archive.progress.snapshottingTitles': '称号をスナップショット中…',
    'archive.progress.collectingPgcr': 'PGCR キャッシュを収集中…',
    'archive.progress.collectingImageUrls': '画像 URL を収集中…',
    'archive.progress.downloadingImages': '画像をダウンロード中 ({done}/{total})…',
    'archive.progress.savingDownload': 'ダウンロードを保存中…',
    'archive.progress.exportComplete': 'アーカイブのエクスポートが完了しました。',
    'archive.progress.updatePrefix': '更新: {message}'
  },
  'zh-chs': {
    'tab.activities': '活动',
    'tab.firsts': '守护者首次',
    'tab.titles': '称号',
    'tab.accomplishments': '成就',
    'tab.breakdown': '活动统计',
    'tab.heatmap': '热图',
    'breakdown.activity': '活动',
    'breakdown.variant': '变体',
    'breakdown.game': '游戏',
    'breakdown.lastPlayed': '最近游玩',
    'breakdown.lastActivityDate': '最近活动',
    'breakdown.elapsedSince': '距今',
    'breakdown.runsClearsTime': '次数 / 通关 / 时间',
    'activities.collapseAllYears': '折叠所有年份',
    'activities.expandAllYears': '展开所有年份',
    'activities.show': '显示',
    'activities.hide': '隐藏',
    'activities.expand': '展开',
    'activities.collapse': '折叠',
    'activities.viewCards': '卡片',
    'activities.viewChronological': '时间线',
    'activities.sortOldest': '最早优先',
    'activities.sortNewest': '最新优先',
    'pgcr.title': '活动报告',
    'pgcr.loading': '正在加载小队…',
    'pgcr.error': '无法加载活动报告。',
    'pgcr.errorRetry': '加载失败。请重试或打开完整报告。',
    'pgcr.noPlayers': '此报告中没有玩家数据。',
    'pgcr.close': '关闭',
    'pgcr.full': '完整 PGCR ↗',
    'pgcr.bungie': '在 Bungie 打开 ↗',
    'pgcr.ctrlHint': 'Ctrl+点击打开完整 PGCR',
    'pgcr.openReportHint': '打开活动报告',
    'common.close': '关闭',
    'archive.offlineBanner': '此设备处于离线模式 — 只读。保存于',
    'archive.exitOffline': '退出离线模式',
    'archive.saveOnDevice': '在此设备上保存以供离线使用',
    'archive.saveOnDeviceHint': '请在个人资料加载后在线使用。无需下载文件 — 仅在此浏览器中有效。',
    'archive.deviceReady': '此设备已可离线使用。请收藏此页面或安装应用（见下方）。',
    'archive.installAppHint': '提示：在 Chrome 或 Edge 中，通过地址栏或浏览器菜单安装应用。',
    'archive.backupSection': '备份或其他电脑',
    'archive.build': '下载备份 (.zip)',
    'archive.buildLite': '下载精简备份（不含 PGCR 小队）',
    'archive.buildHtmlReport': '下载完整 HTML 报告',
    'archive.open': '打开备份文件…',
    'archive.update': '下载更新的备份',
    'archive.exportStarting': '正在开始导出存档…',
    'archive.importStarting': '正在导入存档…',
    'archive.exportFailed': '存档导出失败。',
    'archive.importFailed': '无法导入存档文件。',
    'archive.needProfiles': '创建存档前请至少选择一个个人资料。',
    'archive.offlineSearchDisabled': '离线存档模式下已禁用玩家搜索。',
    'archive.pgcrNotArchived': '此活动报告未包含在存档中。',
    'archive.exportProgress': '正在导出存档…',
    'archive.checkUpdates': '检查更新',
    'archive.checkingUpdates': '正在向 Bungie 检查新活动…',
    'archive.bungieOffline': '无法访问 Bungie API。请连接互联网后重试。',
    'archive.upToDate': '存档已是最新 — 自上次同步以来没有新活动。',
    'archive.updateSuccess': '已用新活动刷新存档。',
    'archive.updateFailed': '无法更新存档。请稍后重试。',
    'archive.syncingAccount': '正在同步',
    'archive.offlineSectionTitle': '此设备离线',
    'archive.d1NamesEnglish': '离线模式下命运1活动名称仍为英文。',
    'archive.progress.prepareDevice': '正在此设备上准备离线副本…',
    'archive.progress.savingImages': '正在保存图片以供离线查看…',
    'archive.progress.activatingOffline': '正在启用离线模式…',
    'archive.progress.readyDevice': '此设备已可离线使用。',
    'archive.progress.buildingZip': '正在创建存档文件…',
    'archive.progress.collectingActivities': '正在收集活动…',
    'archive.progress.collectingFavorites': '正在收集收藏…',
    'archive.progress.buildingManifestSubset': '正在构建清单子集…',
    'archive.progress.snapshottingTitles': '正在快照称号…',
    'archive.progress.collectingPgcr': '正在收集 PGCR 缓存…',
    'archive.progress.collectingImageUrls': '正在收集图片 URL…',
    'archive.progress.downloadingImages': '正在下载图片 ({done}/{total})…',
    'archive.progress.savingDownload': '正在保存下载…',
    'archive.progress.exportComplete': '存档导出完成。',
    'archive.progress.updatePrefix': '更新：{message}'
  }
};

@Injectable({
  providedIn: 'root'
})
export class UiI18nService {
  constructor(private locale: LocaleService) {}

  t(key: string): string {
    const uiLocale = this.mapCultureToUi(this.locale.culture);
    return MESSAGES[uiLocale][key] ?? MESSAGES.en[key] ?? key;
  }

  private mapCultureToUi(culture: string): UiLocale {
    const c = culture.toLowerCase();
    if (c in MESSAGES) {
      return c as UiLocale;
    }
    if (c.startsWith('es')) return 'es';
    if (c.startsWith('zh')) return 'zh-chs';
    if (c.startsWith('pt')) return 'pt-br';
    if (c.startsWith('fr')) return 'fr';
    if (c.startsWith('de')) return 'de';
    if (c.startsWith('ja')) return 'ja';
    return 'en';
  }
}
